/**
 * Builds an environment config object based on the given parameters.
 * This is used to build the environment config object for the environment
 * status table.
 * @param {Object} params - The parameters for the environment config.
 * @param {string} params.name - Display name of the environment.
 * @param {string} params.lowerName - Name used for urls
 * @param {'top-level' | 'subdomain' | 'staging'} params.type - Environment type which determines the base domain and prefix.
 * @returns {Object} The environment config object.
 */
const buildEnvironmentConfig = ({ name, lowerName, type }) => {
	let baseDomain;
	let prefix;

	switch (type) {
		case "top-level":
			baseDomain = "talent.performyard.com";
			prefix = ""; // no subdomain for host (e.g., talent.performyard.com)
			break;
		case "subdomain":
			baseDomain = "talent.performyard.com";
			prefix = lowerName;
			break;
		case "staging":
			baseDomain = "staging.performyard.com";
			prefix = lowerName;
			break;
	}

	const host = `https://${prefix ? `${prefix}.` : ""}${baseDomain}`;
	const config = {
		name,
		lowerName,
		host,
		webServer: `${host}/health`,
	};

	config.apiServer = `https://api.${prefix ? `${prefix}.` : ""}${baseDomain}/health`;
	config.reportingServer = `https://reporting.${prefix ? `${prefix}.` : ""}${baseDomain}/health`;
	config.internalApi = `https://internal-api.${prefix ? `${prefix}.` : ""}${baseDomain}/health`;

	return config;
};

/**
 * Builds an array of environment config objects for production environments.
 * @returns {Object[]} An array of environment config objects.
 */
export const PRODUCTION_ENVIRONMENTS = [
	buildEnvironmentConfig({
		name: "Production",
		lowerName: "production",
		type: "top-level",
	}),
	buildEnvironmentConfig({
		name: "Sandbox",
		lowerName: "sandbox",
		type: "subdomain",
	}),
	buildEnvironmentConfig({
		name: "Demo",
		lowerName: "demo",
		type: "subdomain",
	}),
];

/**
 * Builds an array of environment config objects for testing environments.
 * @returns {Object[]} An array of environment config objects.
 */
export const TESTING_ENVIRONMENTS = [
	buildEnvironmentConfig({
		name: "Preview",
		lowerName: "preview",
		type: "subdomain",
	}),
	buildEnvironmentConfig({
		name: "Staging",
		lowerName: "staging",
		type: "subdomain",
	}),
	...["Han", "Luke", "Leia", "Chewie", "Yoda", "Vader", "Grogu", "Obi-Wan", "Anakin-Skywalker"].map((name) =>
		buildEnvironmentConfig({
			name,
			lowerName: name.toLowerCase().replace(/\s+/g, "-"),
			type: "staging",
		})
	),
];
